import { z } from 'zod';
import { isDateKey } from '@features/quote/utils/dateKey';

// Text first, then coercion: `z.coerce.number()` alone would turn an empty
// prefill into a valid `0` instead of failing the required check.
const requiredWholeNumber = (label: string, min: number, max: number) =>
    z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .pipe(
            z.coerce
                .number()
                .int(`${label} must be a whole number`)
                .min(min, `${label} must be at least ${min}`)
                .max(max, `${label} must be at most ${max}`),
        );

// Bounds mirror ConfirmSmriqVehicleRequest exactly (max:16 / regex / 0..1000000 / 1..200000).
export const smriqVehicleSchema = z.object({
    vrm: z
        .string()
        .trim()
        .min(1, 'Registration is required')
        .max(16, 'Registration must be at most 16 characters')
        .regex(/^[A-Za-z0-9 ]+$/, 'Registration may contain only letters, numbers and spaces'),
    current_mileage: requiredWholeNumber('Current mileage', 0, 1_000_000),
    annual_mileage: requiredWholeNumber('Annual mileage', 1, 200_000),
});

export type SmriqVehicleFormInput = z.input<typeof smriqVehicleSchema>;
export type SmriqVehicleFormOutput = z.output<typeof smriqVehicleSchema>;

// A yes/no choice, not a boolean: an unrestored MOT setting has to be chosen,
// never submitted as a silent `true` the dealer never saw.
const requiredMotChoice = z
    .enum(['', 'yes', 'no'])
    .refine((value) => value !== '', 'Choose whether to include MOT tests')
    .transform((value) => value === 'yes');

const requiredListedNumber = (label: string, allowed: readonly number[]) =>
    z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .pipe(
            z.coerce
                .number()
                .int(`${label} must be a whole number`)
                .refine(
                    (value) => allowed.includes(value),
                    `${label} must be one of ${allowed.join(', ')}`,
                ),
        );

// Bounds come from context.duration — never hard-coded in the browser. A configured
// options list is authoritative there, so min/max no longer apply when one is present.
export const smriqPlanSchema = (min: number, max: number, options?: readonly number[] | null) =>
    z.object({
        duration_months:
            options && options.length > 0
                ? requiredListedNumber('Duration', options)
                : requiredWholeNumber('Duration', min, max),
        include_mot_tests: requiredMotChoice,
        plan_start_date: z.string().refine(isDateKey, 'Plan start date is required'),
    });

export type SmriqPlanFormInput = z.input<ReturnType<typeof smriqPlanSchema>>;
export type SmriqPlanFormOutput = z.output<ReturnType<typeof smriqPlanSchema>>;
